import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardBody } from "reactstrap";
import TableContainer from "Components/Common/TableContainer";
import Icon from "Components/Icons/Icon";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { createSelector } from "reselect";
import { selectTeamMembersState } from "slices/teamMember/reducer";
import { addTeamMember, editTeamMember, getTeamMembers } from "slices/thunks";
import avatar1 from "assets/images/users/userPlaceHolder.png";
import CustomFormModal, { Field } from "Components/Modals/CustomFormModal";
import { useFormik } from "formik";
import { TeamMember } from "types/team.api";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createNameValidation,
} from "Components/constants/validation";
import FullScreenLoader from "Components/Common/FullScreenLoader";

const MembersTable = () => {
  console.log("MembersTable component rendered");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isEditMember, setIsEditMember] = useState<boolean>(false);
  const [openMemberModal, setOpenMemberModal] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const dispatch: any = useAppDispatch();
  const [isToggleLoading, setIsToggleLoading] = useState<boolean>(false);
  const sectionSelector = useMemo(
    () =>
      createSelector(selectTeamMembersState, (state) => ({
        teamMembers: state.teamMembers,
        meta: state.meta,
        isLoading: state.isLoading,
        isEditLoading: state.isEditLoading,
      })),
    []
  );
  const { teamMembers, meta, isLoading, isEditLoading } =
    useAppSelector(sectionSelector);

  const validation = useFormik<TeamMember>({
    enableReinitialize: true,
    initialValues: {
      id: isEditMember && selectedMember ? selectedMember.id : 0,
      name: isEditMember && selectedMember ? selectedMember.name : "",
      email: isEditMember && selectedMember ? selectedMember.email : "",
      password: isEditMember && selectedMember ? selectedMember.password : "",
      active: isEditMember && selectedMember ? selectedMember.active : false,
      avatar: isEditMember && selectedMember ? selectedMember.avatar : null,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH("Name", VALIDATION_LIMITS.NAME.MIN)
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH("Name", VALIDATION_LIMITS.NAME.MAX)
        )
        .test(
          "valid-name",
          createNameValidation("Name").message,
          createNameValidation("Name").test
        ),

      email: Yup.string()
        .email(VALIDATION_MESSAGES.INVALID_EMAIL)
        .required(VALIDATION_MESSAGES.REQUIRED("email")),

      password: Yup.string().when([], {
        is: () => !isEditMember,
        then: (schema) =>
          schema
            .trim()
            .required(VALIDATION_MESSAGES.REQUIRED("password"))
            .min(
              VALIDATION_LIMITS.PASSWORD.MIN,
              VALIDATION_MESSAGES.MIN_LENGTH(
                "Password",
                VALIDATION_LIMITS.PASSWORD.MIN
              )
            )
            .max(
              VALIDATION_LIMITS.PASSWORD.MAX,
              VALIDATION_MESSAGES.MAX_LENGTH(
                "Password",
                VALIDATION_LIMITS.PASSWORD.MAX
              )
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    onSubmit: (values) => {
      const trimmedName = values.name?.trim();
      const trimmedEmail = values.email?.trim();
      const trimmedPassword = values.password?.trim();

      if (isEditMember) {
        dispatch(
          editTeamMember({
            id: values.id,
            name: trimmedName,
            email: trimmedEmail,
            ...(trimmedPassword ? { password: trimmedPassword } : {}),
          })
        );
      } else {
        dispatch(
          addTeamMember({
            name: trimmedName,
            email: trimmedEmail,
            password: trimmedPassword || "",
          })
        );
      }
      validation.resetForm();
      setOpenMemberModal(false);
    },
  });

  const memberFields: Field[] = useMemo(
    () => [
      { name: "name", label: "Name", type: "text", placeholder: "Enter name" },
      {
        name: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter email",
      },
      {
        name: "password",
        label: "Password",
        type: "text",
        placeholder: "Enter password",
      },
    ],
    []
  );

  const handleToggle = useCallback(
    async (id: string, active: boolean) => {
      try {
        if (id && !isEditLoading) {
          setIsToggleLoading(true);
          await dispatch(
            editTeamMember({
              id: Number(id),
              active: active ? 1 : 0,
            })
          )?.unwrap();
        }
      } catch (error) {
        console.error("Error toggling member active status:", error);
      } finally {
        setIsToggleLoading(false);
      }
    },
    [dispatch, isEditLoading]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "USER",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const user = cell?.row?.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={user.avatar || avatar1}
                alt={user.name}
                className="user-img rounded-circle"
                onError={(e) => {
                  e.currentTarget.src = avatar1;
                }}
              />
              <span className="fw-normal text-black2D fs-14 font-primary">
                {user.name ?? "-"}
              </span>
            </div>
          );
        },
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "PASSWORD",
        accessorKey: "password",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            **********
          </span>
        ),
      },
      {
        header: "IS ACTIVE",
        accessorKey: "active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isActive = cell.getValue();
          const rowId = cell?.row?.original?.id;

          return (
            <span
              className={`toggle-switch ${isActive ? "on" : "off"}`}
              onClick={() => handleToggle(rowId, !isActive)}
            >
              <span className={`toggle-label ${isActive ? "on" : "off"}`}>
                {isActive ? "Yes" : "No"}
              </span>
              <div className={`toggle-circle ${isActive ? "on" : "off"}`} />
            </span>
          );
        },
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => (
          <Icon
            name="Edit"
            className="btn-press-effect"
            onClick={() => {
              setIsEditMember(true);
              setSelectedMember(cell?.row?.original);
              setOpenMemberModal(true);
            }}
          />
        ),
      },
    ],
    [handleToggle]
  );

  useEffect(() => {
    const params = searchTerm ? { search: searchTerm } : { page: currentPage };
    dispatch(getTeamMembers(params));
  }, [currentPage, dispatch, searchTerm]);

  return (
    <React.Fragment>
      <FullScreenLoader
        isLoading={isToggleLoading}
        message="Updating member status..."
      />
      <Card id="membersTable" className="br-10 bg-white">
        <CardBody className="pt-0">
          <TableContainer
            columns={columns}
            data={teamMembers || []}
            isGlobalFilter={true}
            customPageSize={meta?.per_page}
            divClass="table-responsive table-card mb-2 bg-white"
            tableClass={`align-middle table-nowrap table-borderless ${
              teamMembers?.length ? "member-table" : ""
            }`}
            thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 member-table"
            theadClass="table-light"
            SearchPlaceholder="Search member"
            onSearch={handleSearch}
            onPageChange={handlePageChange}
            totalPages={meta?.last_page}
            currentPage={meta?.current_page}
            fromPage={meta?.from}
            toPage={meta?.to}
            totalItems={meta?.total}
            searchAlign="left"
            rightContent={
              <CustomButton
                text="New Member"
                leftIcon={<i className="ri-add-line me-1 align-bottom fs-5" />}
                className="bg-primary br-7"
                onClick={() => {
                  setIsEditMember(false);
                  setSelectedMember(null);
                  setOpenMemberModal(true);
                }}
              />
            }
            isInitialLoading={isLoading && !teamMembers?.length}
            isPaginationLoading={isLoading && teamMembers?.length > 0}
          />
        </CardBody>
      </Card>
      <CustomFormModal
        isOpen={openMemberModal}
        onCancel={() => setOpenMemberModal(false)}
        fields={memberFields}
        title={isEditMember ? "Edit Member" : "Add Member"}
        submitText={isEditMember ? "Update Member" : "Add Member"}
        validation={validation}
        isLoading={validation.isSubmitting}
      />
    </React.Fragment>
  );
};

export default MembersTable;
